import { useState, useCallback, useEffect, useRef } from "react";
import type { HailState } from "../types/game";

// Web Speech API types (not in standard DOM lib)
interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  readonly transcript: string;
  readonly confidence: number;
}

interface SpeechRecognitionResultList {
  readonly length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
  [Symbol.iterator](): Iterator<SpeechRecognitionResult>;
}

interface SpeechRecognitionEvent extends Event {
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string;
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionInstance;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _w = typeof window !== "undefined" ? (window as any) : undefined;
const SpeechRecognitionImpl: SpeechRecognitionConstructor | undefined =
  _w?.SpeechRecognition || _w?.webkitSpeechRecognition;

interface Props {
  onSubmit: (action: string) => void;
  disabled: boolean;
  hailState: HailState;
  onToggleMute: () => void;
}

export function CommandInput({ onSubmit, disabled, hailState, onToggleMute }: Props) {
  const [input, setInput] = useState("");
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const speechSupported = !!SpeechRecognitionImpl;

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = input.trim();
      if (!trimmed || disabled) return;
      onSubmit(trimmed);
      setInput("");
    },
    [input, disabled, onSubmit]
  );

  // Speech recognition setup
  useEffect(() => {
    if (!SpeechRecognitionImpl) return;

    const recognition = new SpeechRecognitionImpl();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join("");
      setInput(transcript);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.warn("Speech recognition error:", event.error);
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      recognition.abort();
    };
  }, []);

  const toggleListening = useCallback(() => {
    if (!recognitionRef.current) return;

    if (isListening) {
      recognitionRef.current.stop();
    } else {
      setInput("");
      recognitionRef.current.start();
      setIsListening(true);
    }
  }, [isListening]);

  // Tab hotkey to toggle mute
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab" && hailState.active) {
        e.preventDefault();
        onToggleMute();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hailState.active, onToggleMute]);

  const placeholder = disabled
    ? "Processing..."
    : hailState.active
      ? hailState.channelMuted
        ? "Speaking to bridge crew (muted)..."
        : `Speaking to ${hailState.targetName}...`
      : "Issue orders, Captain...";

  return (
    <div className="command-input-wrapper">
      {/* Channel status bar */}
      {hailState.active && (
        <div className={`channel-status ${hailState.channelMuted ? "channel-muted" : "channel-open"}`}>
          <span className="channel-indicator">
            {hailState.channelMuted ? "CHANNEL MUTED" : `CHANNEL OPEN — ${hailState.targetName}`}
          </span>
          <button
            className={`mute-button ${hailState.channelMuted ? "mute-active" : ""}`}
            onClick={onToggleMute}
            title="Toggle mute (Tab)"
          >
            {hailState.channelMuted ? "UNMUTE" : "MUTE"}
          </button>
        </div>
      )}

      <form className="command-input" onSubmit={handleSubmit}>
        <span className="command-prompt">&gt;</span>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={isListening ? "Listening..." : placeholder}
          disabled={disabled}
          autoFocus
        />
        {speechSupported && (
          <button
            type="button"
            className={isListening ? "voice-active" : ""}
            onClick={toggleListening}
            disabled={disabled}
          >
            {isListening ? "STOP" : "VOICE"}
          </button>
        )}
        <button type="submit" disabled={disabled || !input.trim()}>
          SEND
        </button>
      </form>
    </div>
  );
}
